import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import type {
  CustomerOTPDetailStateType,
  SetCustomerOTPDetailQueryPayloadType,
} from 'features/Account/@types/customerOTP'
import { callErrorMsg } from 'helpers/errorMsg'
import { toastSuccess } from 'utils/toast'
import {
  getCustomerOTPDetail,
  getCustomerOTPReleaseHistory,
  getCustomerOTPReleaseReasons,
  putCustomerOTP,
} from '../../services/customerOTP'

const initialState: CustomerOTPDetailStateType = {
  isLoading: false,
  detail: null,
  releaseHistory: [],
  releaseReasons: [],
  isReleaseModalOpen: false,
  selectedReason: '',
  customReason: '',
  query: {
    cursor: '',
    page_size: 20,
    page_index: 1,
    next_cursor: '',
    previous_cursor: '',
    next_page_exist: false,
    previous_page_exist: false,
  },
}

export const SLICE_NAME = 'customerOTPDetail'

const customerOTPDetailSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    setQuery: (state, action: PayloadAction<SetCustomerOTPDetailQueryPayloadType>) => {
      const { name, value } = action.payload
      state.query[name] = value as never
    },
    openReleaseModal: (state) => {
      state.isReleaseModalOpen = true
      state.selectedReason = ''
      state.customReason = ''
    },
    closeReleaseModal: (state) => {
      state.isReleaseModalOpen = false
    },
    setSelectedReason: (state, action: PayloadAction<string>) => {
      state.selectedReason = action.payload
    },
    setCustomReason: (state, action: PayloadAction<string>) => {
      state.customReason = action.payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCustomerOTPDetail.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchCustomerOTPDetail.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(fetchCustomerOTPDetail.fulfilled, (state, action) => {
        state.isLoading = false
        state.detail = action.payload
      })
      .addCase(fetchCustomerOTPReleaseHistory.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchCustomerOTPReleaseHistory.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(fetchCustomerOTPReleaseHistory.fulfilled, (state, action) => {
        state.isLoading = false
        state.releaseHistory = action.payload.data.blacklist_phone_histories
        state.query.next_cursor = action.payload.pagination.next_cursor
        state.query.previous_cursor = action.payload.pagination.previous_cursor
        state.query.next_page_exist = action.payload.pagination.next_page_exist
        state.query.previous_page_exist = action.payload.pagination.previous_page_exist
      })
      .addCase(fetchCustomerOTPReleaseReasons.fulfilled, (state, action) => {
        state.releaseReasons = action.payload
      })
      .addCase(releaseCustomerOTPDetailBlacklist.pending, (state) => {
        state.isLoading = true
      })
      .addCase(releaseCustomerOTPDetailBlacklist.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(releaseCustomerOTPDetailBlacklist.fulfilled, (state) => {
        state.isLoading = false
      })
  },
})

export default customerOTPDetailSlice.reducer
export const {
  reset,
  setQuery,
  openReleaseModal,
  closeReleaseModal,
  setSelectedReason,
  setCustomReason,
} = customerOTPDetailSlice.actions

export const fetchCustomerOTPDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchCustomerOTPDetail`,
  async (id: number, { rejectWithValue }) => {
    try {
      const res = await getCustomerOTPDetail(id)

      return res.data.data.blacklist_phone
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchCustomerOTPReleaseHistory = createAsyncThunk(
  `${SLICE_NAME}/fetchCustomerOTPReleaseHistory`,
  async (id: number, { rejectWithValue, getState }) => {
    const {
      customerOTPDetail: { query },
    } = getState() as StoreStateType
    try {
      const res = await getCustomerOTPReleaseHistory({
        params: {
          cursor: query.cursor,
          page_size: query.page_size,
          blacklist_phone_number_id: id,
        },
      })
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchCustomerOTPReleaseReasons = createAsyncThunk(
  `${SLICE_NAME}/fetchCustomerOTPReleaseReasons`,
  async (_, { rejectWithValue }) => {
    try {
      const res = await getCustomerOTPReleaseReasons()

      return res.data.data.reasons
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const releaseCustomerOTPDetailBlacklist = createAsyncThunk(
  `${SLICE_NAME}/releaseCustomerOTPDetailBlacklist`,
  async (
    { id, reason }: { id: number; reason: string },
    { rejectWithValue, dispatch, getState },
  ) => {
    const {
      customerOTPDetail: { detail },
    } = getState() as StoreStateType
    const isRelease = !!detail?.is_active
    try {
      await putCustomerOTP({
        id,
        body: { is_active: false, released_reason: reason },
      })

      toastSuccess(isRelease ? 'Berhasil release blacklist' : 'Berhasil mengubah reason')
      dispatch(fetchCustomerOTPDetail(id))
      dispatch(fetchCustomerOTPReleaseHistory(id))
      return id
    } catch (error) {
      callErrorMsg(error, isRelease ? 'Gagal release blacklist' : 'Gagal mengubah reason')
      return rejectWithValue(error)
    }
  },
)
